import React, { useState } from 'react';
import { useApp } from '../../context/AppContext';
import { useBreakpoint } from '../../hooks/useBreakpoint';

const PERSONAS = ['Sales Rep', 'Finance / CFO', 'IT / CTO', 'Marketing', 'Operations', 'C-Suite'];
const MODES = [{ value: 'live', label: 'Live — present with rep notes' }, { value: 'async', label: 'Async — self-guided for buyer' }];

function NewDemoModal({ categories, onClose, onSave }: {
  categories: string[];
  onClose: () => void;
  onSave: (demo: { title: string; category: string; description: string; persona: string; mode: 'live' | 'async' }) => void;
}) {
  const [form, setForm] = useState({ title: '', category: categories[0] ?? 'Product Tour', description: '', persona: PERSONAS[0], mode: 'live' as 'live' | 'async' });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const set = <K extends keyof typeof form>(k: K, v: typeof form[K]) => {
    setForm(prev => ({ ...prev, [k]: v }));
    setErrors(prev => ({ ...prev, [k]: '' }));
  };

  const submit = () => {
    const e: Record<string, string> = {};
    if (!form.title.trim()) e.title = 'Title is required';
    if (!form.description.trim()) e.description = 'Description is required';
    if (Object.keys(e).length) { setErrors(e); return; }
    onSave({ ...form, title: form.title.trim(), description: form.description.trim() });
    onClose();
  };

  const inp = (hasErr: boolean): React.CSSProperties => ({
    width: '100%', padding: '9px 12px', borderRadius: '8px', fontFamily: 'inherit', fontSize: '13px',
    border: `1.5px solid ${hasErr ? '#FC8181' : '#E2E8F0'}`, outline: 'none', boxSizing: 'border-box',
    background: hasErr ? '#FFF5F5' : 'white',
  });
  const lbl: React.CSSProperties = { display: 'block', fontSize: '11px', fontWeight: '700', color: '#4A5568', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: '5px' };
  const sel: React.CSSProperties = { width: '100%', padding: '9px 12px', borderRadius: '8px', border: '1.5px solid #E2E8F0', fontFamily: 'inherit', fontSize: '13px', background: 'white', outline: 'none', cursor: 'pointer' };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,27,45,.55)', zIndex: 900, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }} onClick={onClose}>
      <div style={{ background: 'white', borderRadius: '16px', width: '100%', maxWidth: '460px', boxShadow: '0 24px 64px rgba(0,0,0,.22)', overflow: 'hidden' }} onClick={e => e.stopPropagation()}>
        <div style={{ padding: '18px 20px', borderBottom: '1px solid #E2E8F0', display: 'flex', alignItems: 'center', gap: '10px' }}>
          <div style={{ width: '32px', height: '32px', borderRadius: '9px', background: '#EEF2FF', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <i className="ti ti-presentation-analytics" style={{ fontSize: '16px', color: '#6366F1' }} />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: '15px', fontWeight: '700', color: '#0F1B2D' }}>New Demo</div>
            <div style={{ fontSize: '11px', color: '#718096' }}>Create an interactive demo journey</div>
          </div>
          <button type="button" onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#718096', fontSize: '20px', lineHeight: 1, padding: '2px' }}>×</button>
        </div>
        <div style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
          <div>
            <label htmlFor="demo-title" style={lbl}>Title *</label>
            <input id="demo-title" value={form.title} onChange={e => set('title', e.target.value)} placeholder="e.g. Q3 Enterprise Product Tour" style={inp(!!errors.title)} />
            {errors.title && <div style={{ fontSize: '11px', color: '#E53E3E', marginTop: '3px' }}>{errors.title}</div>}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <div>
              <label htmlFor="demo-category" style={lbl}>Category</label>
              <select id="demo-category" value={form.category} onChange={e => set('category', e.target.value)} style={sel}>
                {categories.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label htmlFor="demo-persona" style={lbl}>Persona</label>
              <select id="demo-persona" value={form.persona} onChange={e => set('persona', e.target.value)} style={sel}>
                {PERSONAS.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label htmlFor="demo-description" style={lbl}>Description *</label>
            <textarea id="demo-description" value={form.description} onChange={e => set('description', e.target.value)} placeholder="What does this demo cover?" rows={3}
              style={{ ...inp(!!errors.description), resize: 'vertical', minHeight: '72px' }} />
            {errors.description && <div style={{ fontSize: '11px', color: '#E53E3E', marginTop: '3px' }}>{errors.description}</div>}
          </div>
          <div>
            <label style={lbl}>Mode</label>
            <div style={{ display: 'flex', gap: '8px' }}>
              {MODES.map(m => (
                <button type="button" key={m.value} onClick={() => set('mode', m.value as 'live' | 'async')}
                  style={{ flex: 1, padding: '10px 12px', borderRadius: '9px', border: `1.5px solid ${form.mode === m.value ? '#6366F1' : '#E2E8F0'}`, background: form.mode === m.value ? '#EEF2FF' : 'white', cursor: 'pointer', fontFamily: 'inherit', textAlign: 'left' }}>
                  <div style={{ fontSize: '12px', fontWeight: '700', color: form.mode === m.value ? '#4F46E5' : '#1A202C' }}>{m.value === 'live' ? 'Live' : 'Async'}</div>
                  <div style={{ fontSize: '10px', color: '#718096', marginTop: '2px' }}>{m.label.split('—')[1].trim()}</div>
                </button>
              ))}
            </div>
          </div>
        </div>
        <div style={{ padding: '14px 20px', borderTop: '1px solid #E2E8F0', display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
          <button type="button" onClick={onClose} style={{ padding: '9px 18px', borderRadius: '8px', border: '1.5px solid #E2E8F0', background: 'white', fontFamily: 'inherit', fontSize: '13px', cursor: 'pointer', color: '#4A5568' }}>Cancel</button>
          <button type="button" onClick={submit} style={{ padding: '9px 20px', borderRadius: '8px', background: '#6366F1', border: 'none', color: 'white', fontFamily: 'inherit', fontSize: '13px', fontWeight: '700', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}>
            <i className="ti ti-plus" /> Create Demo
          </button>
        </div>
      </div>
    </div>
  );
}

export default function DemoHubScreen() {
  const { demoHub, setDemoHub, toast } = useApp();
  const { isMobile, rp } = useBreakpoint();
  const [catFilter, setCatFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [showNewDemo, setShowNewDemo] = useState(false);
  const px = rp({ mobile: '14px', tablet: '18px', desktop: '24px' });

  const filtered = demoHub.demos.filter(d => {
    const matchesCat = catFilter === 'all' || d.category === catFilter;
    const matchesSearch = !search || d.title.toLowerCase().includes(search.toLowerCase()) || d.description.toLowerCase().includes(search.toLowerCase());
    return matchesCat && matchesSearch;
  });
  const totalViews = demoHub.demos.reduce((a, d) => a + d.views, 0);
  const totalVisitors = demoHub.demos.reduce((a, d) => a + d.uniqueVisitors, 0);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: `14px ${px}`, borderBottom: '1px solid var(--border)', flexShrink: 0, background: 'white' }}>
        <div style={{ width: '36px', height: '36px', borderRadius: '10px', background: '#EEF2FF', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <i className="ti ti-presentation-analytics" style={{ fontSize: '18px', color: '#6366F1' }} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: rp({ mobile: '16px', desktop: '20px' }), fontWeight: '800', color: 'var(--navy)' }}>Demo Hub</div>
          {!isMobile && <div style={{ fontSize: '12px', color: 'var(--text3)' }}>Create interactive demo journeys · Share with tracking</div>}
        </div>
        <button onClick={() => setShowNewDemo(true)} style={{ display: 'flex', alignItems: 'center', gap: '5px', padding: isMobile ? '7px 10px' : '8px 16px', borderRadius: '9px', background: '#6366F1', border: 'none', color: 'white', fontFamily: 'inherit', fontSize: '12px', fontWeight: '700', cursor: 'pointer', flexShrink: 0 }}>
          <i className="ti ti-plus" /> {!isMobile && 'New Demo'}
        </button>
      </div>

      {/* Stats bar — 4 cols → 2x2 */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', borderBottom: '1px solid var(--border)', flexShrink: 0, background: 'white' }}>
        {[
          { icon: 'ti-presentation-analytics', label: 'Demos',     val: demoHub.demos.length,        color: '#6366F1', bg: '#EEF2FF' },
          { icon: 'ti-eye',                    label: 'Views',     val: totalViews,                  color: '#0078D4', bg: '#EBF8FF' },
          { icon: 'ti-users',                  label: 'Visitors',  val: totalVisitors,               color: '#22C55E', bg: '#F0FFF4' },
          { icon: 'ti-user-check',             label: 'Sessions',  val: demoHub.sessions.length,     color: '#C8973A', bg: '#FDF4E7' },
        ].map((s, i) => (
          <div key={s.label} style={{ padding: isMobile ? '10px 8px' : '12px 14px', display: 'flex', alignItems: 'center', gap: isMobile ? '6px' : '10px', borderRight: i < 3 ? '1px solid var(--border)' : undefined }}>
            <div style={{ width: isMobile ? '28px' : '32px', height: isMobile ? '28px' : '32px', borderRadius: '8px', background: s.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <i className={`ti ${s.icon}`} style={{ fontSize: isMobile ? '14px' : '15px', color: s.color }} />
            </div>
            <div>
              <div style={{ fontSize: isMobile ? '16px' : '18px', fontWeight: '800', color: 'var(--navy)', lineHeight: 1 }}>{s.val}</div>
              <div style={{ fontSize: '9px', color: 'var(--text3)', fontWeight: '600', textTransform: 'uppercase' }}>{s.label}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Filter + search */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: `8px ${px}`, borderBottom: '1px solid var(--border)', flexShrink: 0, background: 'var(--surface)', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', gap: '4px', overflowX: 'auto', flexShrink: 0, WebkitOverflowScrolling: 'touch' }}>
          {['all', ...demoHub.categories].map(cat => (
            <button key={cat} onClick={() => setCatFilter(cat)} style={{ padding: '5px 11px', borderRadius: '20px', border: '1.5px solid', fontFamily: 'inherit', fontSize: '11px', fontWeight: '500', cursor: 'pointer', background: catFilter === cat ? 'var(--navy)' : 'white', borderColor: catFilter === cat ? 'var(--navy)' : 'var(--border2)', color: catFilter === cat ? 'white' : 'var(--text2)', whiteSpace: 'nowrap', flexShrink: 0 }}>
              {cat === 'all' ? 'All' : cat}
            </button>
          ))}
        </div>
        <div style={{ marginLeft: 'auto', position: 'relative', flexShrink: 0 }}>
          <i className="ti ti-search" style={{ position: 'absolute', left: '8px', top: '50%', transform: 'translateY(-50%)', fontSize: '12px', color: 'var(--text3)' }} />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search…" style={{ padding: '6px 10px 6px 26px', border: '1.5px solid var(--border2)', borderRadius: '8px', fontFamily: 'inherit', fontSize: '12px', width: isMobile ? '120px' : '180px', background: 'white', outline: 'none' }} />
        </div>
      </div>

      {/* Demo cards */}
      <div style={{ flex: 1, overflowY: 'auto', padding: `16px ${px}` }}>
        <div style={{ display: 'grid', gridTemplateColumns: rp({ mobile: '1fr', tablet: '1fr 1fr', desktop: 'repeat(auto-fill,minmax(300px,1fr))' }), gap: '14px', alignContent: 'start' }}>
          {filtered.map(demo => (
            <div key={demo.id} style={{ background: 'white', border: '1.5px solid var(--border)', borderRadius: '14px', overflow: 'hidden' }}>
              <div style={{ height: '130px', background: 'linear-gradient(135deg, #4F46E5, #818CF8)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'default', position: 'relative' }}>
                {demo.persona && <span style={{ position: 'absolute', top: '10px', left: '10px', fontSize: '10px', fontWeight: '700', padding: '3px 9px', borderRadius: '20px', background: 'rgba(0,0,0,.4)', color: 'white' }}>{demo.persona}</span>}
                <span style={{ position: 'absolute', top: '10px', right: '10px', fontSize: '10px', fontWeight: '700', padding: '3px 9px', borderRadius: '20px', background: demo.mode === 'live' ? 'rgba(239,68,68,.8)' : 'rgba(0,0,0,.4)', color: 'white' }}>{demo.mode}</span>
                <i className="ti ti-presentation-analytics" style={{ fontSize: '42px', color: 'rgba(255,255,255,.65)' }} />
              </div>
              <div style={{ padding: '12px 14px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '5px' }}>
                  <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: demo.status === 'live' ? '#22C55E' : '#CBD5E0', display: 'inline-block', flexShrink: 0 }} />
                  <span style={{ fontSize: '13px', fontWeight: '700', color: 'var(--navy)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{demo.title}</span>
                </div>
                <p style={{ fontSize: '11px', color: 'var(--text3)', lineHeight: 1.5, marginBottom: '10px', display: isMobile ? 'none' : 'block' }}>{demo.description}</p>

                {/* Stats */}
                <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
                  {[{ icon: 'ti-eye', val: demo.views, label: 'Views' }, { icon: 'ti-users', val: demo.uniqueVisitors, label: 'Visitors' }, { icon: 'ti-circle-check', val: `${demo.completionRate}%`, label: 'Done' }].map(s => (
                    <div key={s.label} style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: '14px', fontWeight: '700', color: 'var(--navy)' }}>{s.val}</div>
                      <div style={{ fontSize: '9px', color: 'var(--text3)', fontWeight: '600', textTransform: 'uppercase' }}>{s.label}</div>
                    </div>
                  ))}
                </div>

                {/* Secondary */}
                <div style={{ display: 'flex', gap: '4px', marginBottom: '10px' }}>
                  <button type="button" onClick={() => toast(`Editing "${demo.title}"`, 'info')} style={{ flex: 1, padding: '5px 4px', border: '1px solid var(--border2)', borderRadius: '6px', background: 'none', fontFamily: 'inherit', fontSize: '10px', color: 'var(--text3)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '3px' }}>
                    <i className="ti ti-edit" style={{ fontSize: '10px' }} /> Edit
                  </button>
                  <button type="button" onClick={() => { const link = `${window.location.origin}/demo/${demo.slug}`; navigator.clipboard.writeText(link).then(() => toast('Demo link copied!', 'success')); }} style={{ flex: 1, padding: '5px 4px', border: '1px solid var(--border2)', borderRadius: '6px', background: 'none', fontFamily: 'inherit', fontSize: '10px', color: 'var(--text3)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '3px' }}>
                    <i className="ti ti-share" style={{ fontSize: '10px' }} /> Share
                  </button>
                  <button type="button" onClick={() => toast(`Views: ${demo.views} · Visitors: ${demo.uniqueVisitors} · Done: ${demo.completionRate}%`, 'info')} style={{ flex: 1, padding: '5px 4px', border: '1px solid var(--border2)', borderRadius: '6px', background: 'none', fontFamily: 'inherit', fontSize: '10px', color: 'var(--text3)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '3px' }}>
                    <i className="ti ti-chart-bar" style={{ fontSize: '10px' }} /> Stats
                  </button>
                </div>

                {/* Primary CTAs */}
                <div style={{ display: 'flex', gap: '7px' }}>
                  <button type="button" onClick={() => { const items = demo.items as Array<{url?: string}>; const url = items.find(i => i.url)?.url || `${window.location.origin}/demo/${demo.slug}/live`; window.open(url, '_blank'); }} style={{ flex: 1, padding: '9px 6px', borderRadius: '9px', background: 'var(--navy)', border: 'none', color: 'white', fontFamily: 'inherit', fontSize: '11px', fontWeight: '700', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '5px', justifyContent: 'center' }}>
                    <i className="ti ti-player-play-filled" style={{ fontSize: '13px' }} />
                    <div style={{ textAlign: 'left' }}>
                      <div>Present Live</div>
                      <div style={{ fontSize: '8px', opacity: .7 }}>Rep notes</div>
                    </div>
                  </button>
                  <button type="button" onClick={() => { const link = `${window.location.origin}/demo/${demo.slug}`; navigator.clipboard.writeText(link).then(() => toast('Buyer link copied!', 'success')); }} style={{ flex: 1, padding: '9px 6px', borderRadius: '9px', background: '#6366F1', border: 'none', color: 'white', fontFamily: 'inherit', fontSize: '11px', fontWeight: '700', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '5px', justifyContent: 'center' }}>
                    <i className="ti ti-send" style={{ fontSize: '13px' }} />
                    <div style={{ textAlign: 'left' }}>
                      <div>Send to Buyer</div>
                      <div style={{ fontSize: '8px', opacity: .7 }}>Self-guided</div>
                    </div>
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {showNewDemo && (
        <NewDemoModal
          categories={demoHub.categories}
          onClose={() => setShowNewDemo(false)}
          onSave={({ title, category, description, persona, mode }) => {
            const newDemo = {
              id: `demo-${Date.now()}`,
              title, category, description, persona, mode,
              slug: title.toLowerCase().replace(/\s+/g, '-'),
              thumbnail: null, status: 'draft' as const,
              items: [], gate: { enabled: false, fields: [], autoFill: false }, share: {},
              createdAt: new Date().toISOString().slice(0, 10),
              updatedAt: new Date().toISOString().slice(0, 10),
              views: 0, uniqueVisitors: 0, avgTime: '—', completionRate: 0,
            };
            setDemoHub(prev => ({ ...prev, demos: [...prev.demos, newDemo] }));
            toast(`Demo "${title}" created`, 'success');
          }}
        />
      )}
    </div>
  );
}
